import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, Cookie } from "lucide-react";
import Logo from "@/components/Logo";
import { useConsent } from "@/context/ConsentContext";
import NewsletterForm from "@/components/NewsletterForm";
import { isHash, NavItem } from "@/components/SiteHeader";

// Nur real existierende Ziele. Echte Social-Profile von Dimedial.
const SOCIAL = [
  { Icon: Linkedin, href: "https://www.linkedin.com/company/dimedial_agentur", label: "LinkedIn" },
  { Icon: Instagram, href: "https://www.instagram.com/dimedial/", label: "Instagram" },
  { Icon: Facebook, href: "https://www.facebook.com/dimedial/", label: "Facebook" },
];

const COLS = [
  ["Produkt", [["Funktionen", "/#prozess"], ["Quellen", "/quellen"], ["Transparenz & Status", "/transparenz"], ["Sicherheit", "/datenschutz"], ["Preise", "/preise"]]],
  ["Lösungen", [["Redaktionen", "/loesungen/redaktionen"], ["Kommunen", "/loesungen/kommunen"], ["Unternehmen", "/loesungen/unternehmen"], ["Personen", "/loesungen/personen"], ["Agenturen", "/loesungen/agenturen"], ["Privatpersonen", "/loesungen/privatpersonen"]]],
];

const LEGAL = [
  ["Datenschutz", "/datenschutz"], ["Impressum", "/impressum"], ["AGB", "/agb"],
  ["Widerruf", "/widerruf"], ["Verträge kündigen", "/vertrag-kuendigen"],
];

export default function SiteFooter({ variant = "full" }) {
  const { openSettings } = useConsent();

  if (variant === "minimal") {
    return (
      <footer className="border-t border-fc-border bg-white mt-auto" data-testid="site-footer-minimal">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-fc-ink/60">
            <Link to="/datenschutz" className="hover:text-fc-blue">Datenschutz</Link>
            <Link to="/impressum" className="hover:text-fc-blue">Impressum</Link>
            <Link to="/agb" className="hover:text-fc-blue">AGB</Link>
            <button onClick={openSettings} className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-fc-blue" data-testid="footer-cookie-settings">
              <Cookie className="w-4 h-4" /> Cookie-Einstellungen
            </button>
          </div>
          <span className="text-fc-ink/40">© {new Date().getFullYear()} NEWZER – ein Angebot von Dimedial.</span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-fc-ink text-white mt-auto" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <Logo size={32} textSize="text-lg" variant="white" />
          <p className="mt-4 text-sm text-white/60 leading-relaxed">Informationen früh erkennen. Richtig einordnen. Verantwortung tragen.</p>
          <a href="mailto:info@dimedial.de" className="mt-4 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
            <Mail className="w-4 h-4" /> info@dimedial.de
          </a>
          <div className="mt-4 flex items-center gap-3">
            {SOCIAL.map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                className="w-9 h-9 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-white/40">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {COLS.map(([title, links]) => (
          <div key={title}>
            <h4 className="font-head font-semibold text-white text-sm mb-4">{title}</h4>
            <ul className="space-y-2.5">
              {links.map(([label, to]) => (
                <li key={label}>
                  {isHash(to)
                    ? <a href={to.slice(1)} className="text-sm text-white/60 hover:text-white transition-colors">{label}</a>
                    : <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors">{label}</Link>}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h4 className="font-head font-semibold text-white text-sm mb-4">Rechtliches</h4>
          <ul className="space-y-2.5">
            {LEGAL.map(([label, to]) => (
              <li key={label}><Link to={to} className="text-sm text-white/60 hover:text-white transition-colors">{label}</Link></li>
            ))}
            <li>
              <button onClick={openSettings} className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-cookie-settings">
                <Cookie className="w-4 h-4" /> Cookie-Einstellungen
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 grid md:grid-cols-[1fr_minmax(280px,420px)] gap-6 items-center" data-testid="footer-newsletter">
          <div>
            <h4 className="font-head font-semibold text-white text-base">Bei NEWZER auf dem Laufenden bleiben.</h4>
            <p className="mt-1 text-sm text-white/55">Neue Funktionen, Einblicke und Neuigkeiten rund um NEWZER – kompakt per E-Mail.</p>
          </div>
          <NewsletterForm source="website" variant="footer" />
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-6 text-center text-xs text-white/45">
        © {new Date().getFullYear()} NEWZER – ein Angebot von Dimedial. Alle Rechte vorbehalten.
      </div>
    </footer>
  );
}
